import { getAllDASSQuestions } from '../data/dassQuestions'
import { BARC10_QUESTIONS, buildScreener } from '../data/onboarding'

export const TOTAL_STEPS = 5

export const STEP_LABEL = {
  ageCollection: '1 of 5 · You',
  addictionSelection: '2 of 5 · Areas',
  recoveryGate: '3 of 5 · Where you are',
  prioritization: '3 of 5 · Main focus',
  screener: '4 of 5 · Your answers',
  barc10: '4 of 5 · Recovery capital',
  dassAssessment: '5 of 5 · Past week',
  emailCollection: 'Results',
}

/** Is the primary area currently in use (screener path) or in recovery (BARC-10)? */
export function primaryIsActive(state) {
  const g = state.primaryAddiction ? state.gates[state.primaryAddiction] : null
  return g?.status === 'active'
}

/** The question set that follows the gate: 6 screener items or 10 BARC-10 items. */
export function questionCount(state) {
  if (primaryIsActive(state)) return buildScreener(state.primaryAddiction).length
  return BARC10_QUESTIONS.length
}

/** Overall progress (0–1) for the top progress line. */
export function getProgress(state) {
  const areas = Math.max(1, state.selectedAddictions.length)
  const dassTotal = getAllDASSQuestions().length
  const qTotal = Math.max(1, questionCount(state))

  switch (state.currentScreen) {
    case 'ageCollection':
      return 0.04
    case 'addictionSelection':
      return 0.12
    case 'recoveryGate':
      return 0.2 + 0.2 * (state.currentAreaIndex / areas)
    case 'prioritization':
      return 0.42
    case 'screener':
      return 0.46 + 0.26 * (state.currentScreenerIndex / qTotal)
    case 'barc10':
      return 0.46 + 0.26 * (state.currentBarcIndex / qTotal)
    case 'dassAssessment':
      return 0.74 + 0.22 * (state.currentDASSIndex / dassTotal)
    case 'emailCollection':
      return state.resultsId ? 1 : 0.97
    default:
      return 0
  }
}

/**
 * DASS-21 scoring: sum per dimension, doubled. With only the seven-item short
 * form this is an estimate; once the full assessment's items exist all 21 are used.
 */
export function computeDassScores(dassAnswers, part2Answers = {}) {
  const questions = getAllDASSQuestions()
  const sums = { depression: 0, anxiety: 0, stress: 0 }
  questions.forEach((q) => {
    const a = dassAnswers[q.id] ?? part2Answers[q.id]
    if (a !== undefined) sums[q.dimension] += q.answers[a].score
  })
  return {
    depression: sums.depression * 2,
    anxiety: sums.anxiety * 2,
    stress: sums.stress * 2,
    total: (sums.depression + sums.anxiety + sums.stress) * 2,
  }
}

export const RISK_LABEL = {
  low: 'Low',
  moderate: 'Moderate',
  high: 'High',
  severe: 'Very high',
}

export const TIER_LABEL = { 5: 'Very high', 4: 'High', 3: 'Moderate', 2: 'Low', 1: 'Very low' }

export const TIER_DESC = {
  5: 'You have a lot of capacity to absorb pressure and recover from setbacks.',
  4: 'Solid coping under most conditions, with some room to build reserves.',
  3: 'Adequate day to day; sustained pressure is where it starts to wear thin.',
  2: 'Reserves are low right now. Small changes in sleep, load and support tend to help quickly.',
  1: 'Coping capacity is stretched very thin. Talking to a professional would be a good next step.',
}

export const WQ_LABEL = {
  7: 'Exceptional',
  6: 'High',
  5: 'Above average',
  4: 'Average',
  3: 'Below average',
  2: 'Low',
  1: 'Very low',
}
